import { useEffect, useState } from "react";
import ContentLoader from "../../utils/ContentLoader";
import Scroller from "../../utils/Scroller";
import Toggle from "../../utils/Toggle";

const PLAN_TYPE_OPTIONS = [
    { label: "6 Months", value: "6 Months" },
    { label: "12 Months", value: "12 Months" },
    { label: "6 மாதங்கள்", value: "6 மாதங்கள்" },
    { label: "12 மாதங்கள்", value: "12 மாதங்கள்" },
];

const SIX_MONTH_TYPES = ["6 Months", "6 மாதங்கள்"];
const TWELVE_MONTH_TYPES = ["12 Months", "12 மாதங்கள்"];
const TOTAL_DAYS = { 6: 182, 12: 365 };

function addMonths(dateStr, months) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + months);
    return d.toISOString().slice(0, 10);
}

function addDays(dateStr, days) {
    if (!dateStr || days == null) return "";
    const d = new Date(dateStr);
    d.setDate(d.getDate() + Number(days));
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function calcReaded(startDate, completedDays) {
    if (!startDate || !completedDays) return "";
    return addDays(startDate, completedDays);
}

function getPlanMonths(planType) {
    if (SIX_MONTH_TYPES.includes(planType)) return 6;
    if (TWELVE_MONTH_TYPES.includes(planType)) return 12;
    return null;
}

function calcEndAndRemaining(startDate, planType, completedDays) {
    const months = getPlanMonths(planType);
    if (!months || !startDate) return {};
    const end_date = addMonths(startDate, months);
    const totalDays = TOTAL_DAYS[months];
    const remaining_days = Math.max(0, totalDays - (completedDays || 0));
    return { end_date, remaining_days };
}

function todayStr() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function calcFromRemaining(remainingDays, planType) {
    const months = getPlanMonths(planType);
    if (!months) return {};
    const totalDays = TOTAL_DAYS[months];
    const completed_days = Math.max(0, totalDays - remainingDays);
    return { completed_days, readed: todayStr() };
}

const PLAN_ORDER_OPTIONS = [
    { label: "Normal", value: "normal" },
    { label: "Shuffled", value: "shuffled" },
];

const selectStyles = {
    control: (provided) => ({
        ...provided,
        height: "44px",
        minHeight: "44px",
        borderRadius: "6px",
    }),
};

export default function ReadingPlanForm({ onSubmit, initialData = {}, userOptions = [], usersLoading = false, onUserInputChange, onUserSelect, loading }) {
    const isEdit = Boolean(initialData?.id);

    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        user_id: "",
        plan_type: "",
        normalOrShuffeled: "normal",
        is_start: true,
        start_date: "",
        end_date: "",
        completed_days: 0,
        remaining_days: 0,
        current_id: 0,
        six_months_flag: 0,
        current_date: "",
        readed: "",
    });

    useEffect(() => {
        if (initialData?.id) {
            setFormData({
                user_id: initialData.user_id ?? "",
                plan_type: initialData.plan_type ?? "",
                normalOrShuffeled: initialData.normalOrShuffeled ?? "normal",
                is_start: initialData.is_start ?? true,
                start_date: initialData.start_date ? initialData.start_date.slice(0, 10) : "",
                end_date: initialData.end_date ? initialData.end_date.slice(0, 10) : "",
                completed_days: initialData.completed_days ?? 0,
                remaining_days: initialData.remaining_days ?? 0,
                current_id: initialData.current_id ?? 0,
                six_months_flag: initialData.six_months_flag ?? 0,
                current_date: initialData.current_date ? initialData.current_date.slice(0, 10) : "",
                readed: initialData.readed ? initialData.readed.slice(0, 10) : "",
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const parsed = type === "number" ? (value === "" ? 0 : parseInt(value, 10)) : value;

        setFormData((prev) => {
            const updated = { ...prev, [name]: parsed };
            if (name === "start_date") {
                const auto = calcEndAndRemaining(parsed, prev.plan_type, prev.completed_days);
                Object.assign(updated, auto);
                updated.readed = calcReaded(parsed, prev.completed_days);
            }
            if (name === "completed_days") {
                updated.readed = calcReaded(prev.start_date, parsed);
            }
            if (name === "remaining_days") {
                const auto = calcFromRemaining(parsed, prev.plan_type);
                Object.assign(updated, auto);
            }
            return updated;
        });
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const newErrors = {};
        if (!isEdit && !formData.user_id) newErrors.user_id = "User is required";
        if (!formData.plan_type) newErrors.plan_type = "Plan type is required";
        if (!formData.normalOrShuffeled) newErrors.normalOrShuffeled = "Plan order is required";
        if (!formData.start_date) newErrors.start_date = "Start date is required";
        if (!formData.end_date) newErrors.end_date = "End date is required";
        if (formData.completed_days < 0) newErrors.completed_days = "Cannot be negative";
        if (formData.remaining_days < 0) newErrors.remaining_days = "Cannot be negative";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        onSubmit(formData);
    };

    if (loading) return <ContentLoader />;

    return (
        <div className="col-lg-12">
            <div className="card" style={{ marginTop: "10px" }}>
                <div className="card-header">
                    <h5 className="card-title mb-0">
                        {isEdit ? "Edit Reading Plan" : "Create Reading Plan"}
                    </h5>
                </div>
                <div className="card-body">
                    <form className="row gy-3" onSubmit={handleSubmit} noValidate>

                        {/* User (only on create) */}
                        {!isEdit && (
                            <div className="col-md-6">
                                <label className="form-label">
                                    User <span style={{ color: "red" }}>*</span>
                                </label>
                                <Scroller
                                    options={userOptions}
                                    value={userOptions.find((u) => u.value === formData.user_id) || null}
                                    onChange={(sel) => {
                                        setFormData((prev) => ({ ...prev, user_id: sel?.value || "" }));
                                        setErrors((prev) => ({ ...prev, user_id: "" }));
                                        onUserSelect?.(sel);
                                    }}
                                    onInputChange={onUserInputChange}
                                    isLoading={usersLoading}
                                    noOptionsMessage={() => "Type to search users"}
                                    placeholder="Search by name or email..."
                                    isClearable={false}
                                    styles={selectStyles}
                                />
                                {errors.user_id && <div className="text-danger" style={{ fontSize: "0.875em", marginTop: "4px" }}>{errors.user_id}</div>}
                            </div>
                        )}

                        {/* Plan Type */}
                        <div className="col-md-6">
                            <label className="form-label">
                                Plan Type <span style={{ color: "red" }}>*</span>
                            </label>
                            <Scroller
                                options={PLAN_TYPE_OPTIONS}
                                value={PLAN_TYPE_OPTIONS.find((o) => o.value === formData.plan_type) || null}
                                onChange={(sel) => {
                                    const planType = sel?.value || "";
                                    setFormData((prev) => {
                                        const auto = calcEndAndRemaining(prev.start_date, planType, prev.completed_days);
                                        return { ...prev, plan_type: planType, ...auto };
                                    });
                                    setErrors((prev) => ({ ...prev, plan_type: "" }));
                                }}
                                placeholder="Select Plan Type"
                                isClearable={false}
                                styles={selectStyles}
                            />
                            {errors.plan_type && <div className="text-danger" style={{ fontSize: "0.875em", marginTop: "4px" }}>{errors.plan_type}</div>}
                        </div>

                        {/* Plan Order */}
                        <div className="col-md-6">
                            <label className="form-label">
                                Plan Order <span style={{ color: "red" }}>*</span>
                            </label>
                            <Scroller
                                options={PLAN_ORDER_OPTIONS}
                                value={PLAN_ORDER_OPTIONS.find((o) => o.value === formData.normalOrShuffeled) || null}
                                onChange={(sel) => {
                                    setFormData((prev) => ({ ...prev, normalOrShuffeled: sel?.value || "normal" }));
                                    setErrors((prev) => ({ ...prev, normalOrShuffeled: "" }));
                                }}
                                placeholder="Select Order"
                                isClearable={false}
                                styles={selectStyles}
                            />
                            {errors.normalOrShuffeled && <div className="text-danger" style={{ fontSize: "0.875em", marginTop: "4px" }}>{errors.normalOrShuffeled}</div>}
                        </div>

                        {/* Start Date */}
                        <div className="col-md-6">
                            <label className="form-label">
                                Start Date <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                type="date"
                                name="start_date"
                                className={`form-control ${errors.start_date ? "is-invalid" : ""}`}
                                value={formData.start_date}
                                onChange={handleChange}
                            />
                            {errors.start_date && <div className="invalid-feedback">{errors.start_date}</div>}
                        </div>

                        {/* End Date */}
                        <div className="col-md-6">
                            <label className="form-label">
                                End Date <span style={{ color: "red" }}>*</span>
                            </label>
                            <input
                                type="date"
                                name="end_date"
                                className={`form-control ${errors.end_date ? "is-invalid" : ""}`}
                                value={formData.end_date}
                                onChange={handleChange}
                            />
                            {errors.end_date && <div className="invalid-feedback">{errors.end_date}</div>}
                        </div>

                        {/* Completed Days */}
                        <div className="col-md-6">
                            <label className="form-label">Completed Days</label>
                            <input
                                type="number"
                                name="completed_days"
                                min="0"
                                className={`form-control ${errors.completed_days ? "is-invalid" : ""}`}
                                value={formData.completed_days}
                                onChange={handleChange}
                            />
                            {errors.completed_days && <div className="invalid-feedback">{errors.completed_days}</div>}
                        </div>

                        {/* Remaining Days */}
                        <div className="col-md-6">
                            <label className="form-label">Remaining Days</label>
                            <input
                                type="number"
                                name="remaining_days"
                                min="0"
                                className={`form-control ${errors.remaining_days ? "is-invalid" : ""}`}
                                value={formData.remaining_days}
                                onChange={handleChange}
                            />
                            {errors.remaining_days && <div className="invalid-feedback">{errors.remaining_days}</div>}
                        </div>

                        {/* Current ID */}
                        <div className="col-md-6">
                            <label className="form-label">Current ID</label>
                            <input
                                type="number"
                                name="current_id"
                                min="0"
                                className="form-control"
                                value={formData.current_id}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Six Months Flag */}
                        <div className="col-md-6">
                            <label className="form-label">Six Months Flag</label>
                            <input
                                type="number"
                                name="six_months_flag"
                                min="0"
                                className="form-control"
                                value={formData.six_months_flag}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Current Date */}
                        <div className="col-md-6">
                            <label className="form-label">Current Date</label>
                            <input
                                type="date"
                                name="current_date"
                                className="form-control"
                                value={formData.current_date}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Last Read At */}
                        <div className="col-md-6">
                            <label className="form-label">Last Read At</label>
                            <input
                                type="date"
                                name="readed"
                                className="form-control"
                                value={formData.readed}
                                onChange={handleChange}
                            />
                        </div>

                        {/* Is Start */}
                        <div className="col-md-6" style={{ marginTop: "10px" }}>
                            <label className="form-label mb-2">Is Started</label>
                            <div>
                                <Toggle
                                    value={formData.is_start}
                                    onChange={(val) => setFormData((prev) => ({ ...prev, is_start: val }))}
                                />
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="col-12 d-flex justify-content-end mt-3">
                            <button className="btn btn-primary-600" type="submit" style={{ borderRadius: "4px" }}>
                                {isEdit ? "Update Reading Plan" : "Create Reading Plan"}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}
